/**
 * MeiProfissaoPage - Template Programático SEO
 * Gera páginas únicas para cada profissão/nicho de MEI
 * 
 * Uso: /mei/:profissao
 * Exemplo: /mei/cabeleireiro, /mei/eletricista
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FileText, CheckCircle, ArrowRight, DollarSign, Building2,
  ChevronDown, ChevronUp, Zap, Award,
  Calculator, Briefcase, TrendingUp, Lightbulb
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { profissoesData } from './profissoes-mei-data';

const MeiProfissaoPage = () => {
  const { profissao } = useParams();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  
  const slug = profissao?.toLowerCase().replace(/\s+/g, '-') || '';
  const data = profissoesData[slug];
  
  const navigateTo = (url) => {
    if (url.startsWith('http')) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };
  
  // Página 404 se profissão não encontrada
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            MEI para "{profissao}"
          </h1>
          <p className="text-gray-600 mb-8">
            Estamos preparando conteúdo para esta profissão.
          </p>
          <button
            onClick={() => navigate('/como-abrir-mei')}
            className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
          >
            Ver Guia de Como Abrir MEI
          </button>
        </div>
        <Footer />
      </div>
    );
  }
  
  const pageTitle = `MEI ${data.titulo} 2026: Como Abrir, CNAE ${data.cnae}, Nota Fiscal | MeuMei`;
  const pageDescription = `Guia completo ${data.titulo} MEI. CNAE ${data.cnae}, como abrir, emitir nota fiscal, DAS. Faturamento: ${data.faturamento_medio}. Comece grátis!`;

  return (
    <div className="min-h-screen font-sans bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://meumei.net/mei/${slug}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://meumei.net/mei/${slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": pageTitle,
          "description": pageDescription,
          "author": { "@type": "Organization", "name": "MeuMei" },
          "publisher": { "@type": "Organization", "name": "MeuMei" },
          "dateModified": new Date().toISOString().split('T')[0]
        })
      }} />

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faq.map(item => ({
            "@type": "Question",
            "name": item.p,
            "acceptedAnswer": { "@type": "Answer", "text": item.r }
          }))
        })
      }} />

      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/mei" className="hover:text-white">MEI por Profissão</a>
            <span className="mx-2">›</span>
            <span className="text-white">{data.titulo}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
              {data.categoria}
            </span>
            
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              MEI para {data.titulo}
            </h1>
            
            <p className="text-lg md:text-xl opacity-90 mb-6 max-w-2xl mx-auto">
              {data.descricao}. Guia completo com CNAE, como abrir, nota fiscal e muito mais.
            </p>

            {/* Info Cards */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                <Briefcase className="w-5 h-5" />
                <span className="text-sm font-medium">CNAE {data.cnae}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                <DollarSign className="w-5 h-5" />
                <span className="text-sm font-medium">{data.faturamento_medio}</span>
              </div>
            </div>

            {/* CTA */}
            <motion.button
              onClick={() => navigateTo('https://app.meumei.net/register')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-2xl shadow-lg inline-flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Abrir MEI {data.titulo} Grátis
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        
        {/* O que faz */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            O que faz um {data.titulo} MEI?
          </h2>
          <p className="text-gray-600 text-lg mb-6">{data.descricao}</p>
          
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Serviços mais comuns
            </h3>
            <div className="flex flex-wrap gap-2">
              {data.servicos.map((servico) => (
                <span key={servico} className="px-4 py-2 bg-white border rounded-full text-gray-700">
                  {servico}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CNAE */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            CNAE para {data.titulo} MEI
          </h2>
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-emerald-800 text-lg">CNAE Principal: {data.cnae}</p>
                <p className="text-emerald-700 mt-1">
                  Código de atividade econômica permitido para {data.titulo} no MEI.
                </p>
                {data.cnaes_secundarios && data.cnaes_secundarios.length > 0 && (
                  <p className="text-emerald-600 text-sm mt-2">
                    CNAEs secundários: {data.cnaes_secundarios.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Quanto custa */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Quanto custa ser {data.titulo} MEI?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Calculator className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-blue-800">DAS Mensal</h3>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">R$ 75,90</p>
              <p className="text-blue-700 text-sm">INSS + ISS (serviços)</p>
            </div>
            
            <div className="bg-purple-50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-purple-800">Faturamento Médio</h3>
              </div>
              <p className="text-2xl font-bold text-purple-600 mb-2">{data.faturamento_medio}</p>
              <p className="text-purple-700 text-sm">Potencial de ganho como MEI</p>
            </div>
          </div>
        </section>

        {/* Dicas */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Dicas para {data.titulo} MEI ter Sucesso
          </h2>
          
          <div className="space-y-4">
            {data.dicas.map((dica, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start p-4 bg-amber-50 rounded-xl"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-gray-700">{dica}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Equipamentos */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Equipamentos para {data.titulo}
          </h2>
          
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex flex-wrap gap-3">
              {data.equipamentos.map((equip) => (
                <span key={equip} className="px-4 py-2 bg-white border rounded-lg text-gray-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  {equip}
                </span>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Investimento inicial estimado: {data.investimento_inicial}
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Perguntas Frequentes: {data.titulo} MEI
          </h2>

          <div className="space-y-4">
            {data.faq.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-800 pr-4">{item.p}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-600 leading-relaxed">{item.r}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Final */}
        <section className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">
                Pronto para abrir seu MEI de {data.titulo}?
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                Com o MeuMei você abre seu MEI grátis, emite notas fiscais ilimitadas 
                e gerencia todo seu negócio em um só lugar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  onClick={() => navigateTo('https://app.meumei.net/register')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  Criar Conta Grátis
                </motion.button>
                <motion.button
                  onClick={() => navigateTo('/planos')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white/20 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all"
                >
                  Ver Planos
                </motion.button>
              </div>
              <p className="text-sm opacity-75 mt-4">
                ✓ 7 dias grátis &nbsp; ✓ Sem cartão &nbsp; ✓ Cancele quando quiser
              </p>
            </div>
          </motion.div>
        </section>

        {/* Links Relacionados */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Conteúdos Relacionados</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <a href="/como-abrir-mei" className="p-4 border rounded-xl hover:border-emerald-500 transition-colors flex items-center gap-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-700">Como Abrir MEI - Guia Completo</span>
            </a>
            <a href="/nota-fiscal-mei" className="p-4 border rounded-xl hover:border-emerald-500 transition-colors flex items-center gap-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-700">Como Emitir Nota Fiscal MEI</span>
            </a>
            <a href="/das-mei" className="p-4 border rounded-xl hover:border-emerald-500 transition-colors flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-700">DAS MEI 2026 - Valor Atualizado</span>
            </a>
            <a href="/mei-inss" className="p-4 border rounded-xl hover:border-emerald-500 transition-colors flex items-center gap-3">
              <Award className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-700">Benefícios do MEI no INSS</span>
            </a>
          </div>
        </section>
      </article>

      {/* Espaço para mobile */}
      <div className="h-24 md:hidden" />

      <Footer />
    </div>
  );
};

export default MeiProfissaoPage;